import * as _ from 'lodash';
import { RoomFactory } from 'Plugins/FloorPlan/Helpers/BaseRoomFactory';

import { ObjectUtils } from '../../../../Helpers/ObjectUtils';
import { OpeningFactory } from '../../Helpers/BaseOpeningFactory';
import { RoomItemFactory } from '../../Helpers/BaseRoomItemFactory';
import { MapConstants } from '../../MapConstants';
import { IOpening } from '../../Models/IOpening';
import { IRoom, RoomAngle, RoomLocalisation, RoomShape, RoomType } from '../../Models/IRoom';
import { IRoomItem } from '../../Models/IRoomItem';
import { InvalidPositionDetection } from '../Collisions/InvalidPositionDetection';
import { IdGenerator } from '../IdGenerator';
import { OpeningUtils } from '../Opening/OpeningUtils';
import { PerimeterCalc } from '../PerimeterCalc';
import { PointUtils } from '../PointUtils';
import { qSVG } from '../qSVG';
import { CoordPoint, Wall } from '../Types';
import { WallUtils } from '../Wall/WallUtils';

type CreateResult = { room: IRoom; roomWalls?: Array<Wall> };

type DuplicateParams = { walls?: Array<Wall>; openings?: Array<IOpening>; roomItems?: Array<IRoomItem> };

export class RoomUtils {
    public static createRoom = (room: IRoom, currentWalls: Wall[]): CreateResult => {
        room.roomId = room.roomId ?? IdGenerator.generate();

        if (room.type === RoomType.Wall) {
            room.localisation = RoomLocalisation.Inside;
        }
        if (room.coords) {
            return this.createCustomShapeRoom(room, currentWalls);
        } else {
            //! ⬇️ obsolete kept for v2 export compat
            const shape = toShape(room);
            switch (shape) {
                case RoomShape.Rectangle:
                    return this.createRectangleShapeRoom(room, currentWalls);
                case RoomShape.Diamond:
                    return this.createDiamondShapeRoom(room, room.angle!, currentWalls);
                case RoomShape.Flyingwing:
                    return this.createFlyingWingShapeRoom(room, room.angle!, currentWalls);
                case RoomShape.U:
                    return this.createUShapeRoom(room, room.angle!, currentWalls);
                case RoomShape.Plus:
                    return this.createPlusShapeRoom(room, currentWalls);

                case RoomShape.Custom:
                    return this.createCustomShapeRoom(room, currentWalls);
            }
        }
        return { room, roomWalls: [] };
    };

    public static exportRoom = (room: IRoom): IRoom => {
        return this.exportCustomShapeRoom(room)!;
    };

    public static duplicate(roomId: string, rooms: IRoom[], params?: DuplicateParams): CreateResult {
        const room = ObjectUtils.clone(rooms.find((r) => r.roomId === roomId))!;
        const name = RoomFactory.isDefaultName(room.name)
            ? RoomFactory.getDuplicateName(room, rooms)
            : RoomFactory.toUniqueName(rooms, room.roomType, room.roomFloor);

        const { walls = [], openings = [], roomItems = [] } = params || {};
        let exportedRoom: IRoom = this.exportRoom(room);
        if (exportedRoom) {
            exportedRoom.roomId = IdGenerator.generate();
            exportedRoom.name = name;

            if ([RoomType.Sol, RoomType.Wall].includes(exportedRoom.type!)) {
                exportedRoom.contournements = ObjectUtils.clone(exportedRoom.contournements);

                const bbox = PointUtils.calculateBoundingBox({ points: room.coords! })!;
                const overallBbox = PointUtils.calculateBoundingBox({ walls })!;
                const offset = {
                    x: qSVG.measure({ x: bbox.xMin, y: bbox.yMin }, { x: overallBbox.xMax, y: bbox.yMin }) / 100 + 1.8,
                    y: 0,
                };
                const offsetInCm = {
                    x: offset.x * 100,
                    y: 0,
                };
                const centerPositionOffset = PointUtils.translation(
                    { x: exportedRoom.center!.x, y: exportedRoom.center!.y },
                    offset
                );
                exportedRoom.coords = exportedRoom.coords?.map((x) => PointUtils.translation(x, offsetInCm));
                exportedRoom.center = centerPositionOffset;
                PointUtils.smoothOutPoint(exportedRoom.center);

                const positionOffset = { ...offset };
                positionOffset.x = offset.x * 100;

                const exportedRoomOpenings = OpeningUtils.updateRoomOpenings(room, openings);
                exportedRoom.openings = ObjectUtils.clone(exportedRoomOpenings);
                const newOpenings = ObjectUtils.clone(openings);
                for (const opening of exportedRoom.openings!) {
                    opening.openingId = IdGenerator.generate();
                    opening.roomId = exportedRoom.roomId;
                    opening.name = OpeningFactory.getNewName(opening, newOpenings);
                    opening.x = opening.x! + positionOffset.x;
                    newOpenings.push(opening);
                }

                exportedRoom.roomItems = ObjectUtils.clone(exportedRoom.roomItems);
                const newRoomItems = ObjectUtils.clone(roomItems);
                for (const roomItem of exportedRoom.roomItems!) {
                    roomItem.roomItemId = IdGenerator.generate();
                    roomItem.roomId = exportedRoom.roomId;

                    roomItem.coordsReal = roomItem.coordsReal?.map((x) => PointUtils.translation(x, positionOffset));
                    roomItem.name = RoomItemFactory.getNewName(roomItem, newRoomItems);
                    newRoomItems.push(roomItem);
                }
            }

            if ([RoomType.Stairs].includes(exportedRoom.type!)) {
                exportedRoom.stairsSteps = ObjectUtils.clone(exportedRoom.stairsSteps);
            }

            return this.createRoom(exportedRoom, walls);
        }

        //refreshWallsMinimumSizeAndOriginalAngle(ROOM[ROOM.length - 1].walls);
        //WallUtils.refreshWallsMinimumSizeAndOriginalAngle(walls, newOpenings);

        return { room: exportedRoom! };
    }

    //#region //* CREATE ROOM SHAPE

    private static createRectangleShapeRoom = (room: IRoom, currentWalls: Wall[]): CreateResult => {
        let points: Array<CoordPoint>;
        if (room.coords) {
            points = room.coords;
        } else {
            points = [
                { x: 0, y: 0 },
                { x: room.obsoleteDimensions!.width!, y: 0 },
                { x: room.obsoleteDimensions!.width!, y: room.obsoleteDimensions!.height! },
                { x: 0, y: room.obsoleteDimensions!.height! },
            ];
            if (room.center) {
                PointUtils.translationByMeanPoint(points, room.center);
            }
            points.forEach((point) => PointUtils.convertToCoordinates(point));
            if (!room.center) {
                PointUtils.placeToZero(points);
                PointUtils.placeAlongSideExistingRooms(points, currentWalls);
            }
            PointUtils.smoothOutPoints(points);
            room.coords = [...points, points[0]];
        }

        room.shape = RoomShape.Custom;
        const roomWalls = [
            WallUtils.createWall(points[0], points[1], 'side_0', room.roomId!),
            WallUtils.createWall(points[1], points[2], 'side_1', room.roomId!),
            WallUtils.createWall(points[2], points[3], 'side_2', room.roomId!),
            WallUtils.createWall(points[3], points[0], 'side_3', room.roomId!),
        ];
        return { room, roomWalls };
    };

    private static createDiamondShapeRoom = (room: IRoom, angle: RoomAngle, currentWalls: Wall[]): CreateResult => {
        let points: Array<CoordPoint>;
        if (room.coords) {
            points = room.coords;
        } else {
            //* define points coordinates
            const tableCommonPoint = Math.sqrt(Math.pow(room.obsoleteDimensions!.table!, 2) / 2);

            points = [
                { x: 0, y: tableCommonPoint },
                { x: tableCommonPoint, y: 0 },
                { x: tableCommonPoint + room.obsoleteDimensions!.right_crown!, y: 0 },
                {
                    x: tableCommonPoint + room.obsoleteDimensions!.right_crown!,
                    y: tableCommonPoint + room.obsoleteDimensions!.left_crown!,
                },
                { x: 0, y: tableCommonPoint + room.obsoleteDimensions!.left_crown! },
            ];

            PointUtils.rotation(points, angle);

            //* move points to correct location
            if (room.center) {
                PointUtils.translationByMeanPoint(points, room.center);
            }

            //* convert meter to svg coordinates
            points.forEach((point) => PointUtils.convertToCoordinates(point, true));

            if (!room.center) {
                PointUtils.placeToZero(points);
                PointUtils.placeAlongSideExistingRooms(points, currentWalls);
            }

            PointUtils.smoothOutPoints(points);
            room.coords = [...points, points[0]];
        }

        room.shape = RoomShape.Custom;
        const roomWalls = [
            //* draw table
            WallUtils.createWall(points[0], points[1], 'side_0', room.roomId!),
            //* draw right crown
            WallUtils.createWall(points[1], points[2], 'side_1', room.roomId!),
            //* draw right pavilion
            WallUtils.createWall(points[2], points[3], 'side_2', room.roomId!),
            //* draw left pavilion
            WallUtils.createWall(points[3], points[4], 'side_3', room.roomId!),
            //* draw left crown
            WallUtils.createWall(points[4], points[0], 'side_4', room.roomId!),
        ];
        return { room, roomWalls };
    };

    private static createFlyingWingShapeRoom = (room: IRoom, angle: RoomAngle, currentWalls: Wall[]): CreateResult => {
        let points: Array<CoordPoint>;
        if (room.coords) {
            points = room.coords;
        } else {
            points = [
                { x: 0, y: 0 },
                { x: room.obsoleteDimensions!.left_wingtip! + room.obsoleteDimensions!.right_trailing_edge!, y: 0 },
                {
                    x: room.obsoleteDimensions!.left_wingtip! + room.obsoleteDimensions!.right_trailing_edge!,
                    y: room.obsoleteDimensions!.right_wingtip!,
                },
                { x: room.obsoleteDimensions!.left_wingtip!, y: room.obsoleteDimensions!.right_wingtip! },
                {
                    x: room.obsoleteDimensions!.left_wingtip!,
                    y: room.obsoleteDimensions!.right_wingtip! + room.obsoleteDimensions!.left_trailing_edge!,
                },
                { x: 0, y: room.obsoleteDimensions!.right_wingtip! + room.obsoleteDimensions!.left_trailing_edge! },
            ];
            if (angle) {
                PointUtils.rotation(points, angle);
            }

            if (room.center) {
                PointUtils.translationByMeanPoint(points, room.center);
            }

            points.forEach((point) => PointUtils.convertToCoordinates(point));
            if (!room.center) {
                PointUtils.placeToZero(points);
                PointUtils.placeAlongSideExistingRooms(points, currentWalls);
            }
            PointUtils.smoothOutPoints(points);
            room.coords = [...points, points[0]];
        }

        room.shape = RoomShape.Custom;
        const roomWalls = [
            WallUtils.createWall(points[0], points[1], 'side_0', room.roomId!),
            WallUtils.createWall(points[1], points[2], 'side_1', room.roomId!),
            WallUtils.createWall(points[2], points[3], 'side_2', room.roomId!),
            WallUtils.createWall(points[3], points[4], 'side_3', room.roomId!),
            WallUtils.createWall(points[4], points[5], 'side_4', room.roomId!),
            WallUtils.createWall(points[5], points[0], 'side_5', room.roomId!),
        ];
        return { room, roomWalls };
    };

    private static createUShapeRoom = (room: IRoom, angle: RoomAngle, currentWalls: Wall[]): CreateResult => {
        const exteriorBase = room.obsoleteDimensions!.exterior_base!;
        const leftExteriorEdge = room.obsoleteDimensions!.left_exterior_edge!;
        const leftTip = room.obsoleteDimensions!.left_tip!;
        const leftInteriorEdge = room.obsoleteDimensions!.left_interior_edge!;
        const rightTip = room.obsoleteDimensions!.right_tip!;
        const rightExteriorEdge = room.obsoleteDimensions!.right_exterior_edge!;

        //* define points coordinates
        let points: Array<CoordPoint>;
        if (room.coords) {
            points = room.coords;
        } else {
            points = [
                { x: 0, y: 0 },
                { x: leftTip, y: 0 },
                { x: leftTip, y: leftInteriorEdge },
                { x: exteriorBase - rightTip, y: leftInteriorEdge },
                { x: exteriorBase - rightTip, y: leftExteriorEdge - rightExteriorEdge },
                { x: exteriorBase, y: leftExteriorEdge - rightExteriorEdge },
                { x: exteriorBase, y: leftExteriorEdge },
                { x: 0, y: leftExteriorEdge },
            ];

            PointUtils.rotation(points, angle);

            if (room.center) {
                PointUtils.translationByMeanPoint(points, room.center);
            }

            points.forEach((point) => PointUtils.convertToCoordinates(point));

            if (!room.center) {
                PointUtils.placeToZero(points);
                PointUtils.placeAlongSideExistingRooms(points, currentWalls);
            }
            PointUtils.smoothOutPoints(points);
            room.coords = [...points, points[0]];
        }

        room.shape = RoomShape.Custom;
        const roomWalls = [
            WallUtils.createWall(points[0], points[1], 'side_0', room.roomId!),
            WallUtils.createWall(points[1], points[2], 'side_1', room.roomId!),
            WallUtils.createWall(points[2], points[3], 'side_2', room.roomId!),
            WallUtils.createWall(points[3], points[4], 'side_3', room.roomId!),
            WallUtils.createWall(points[4], points[5], 'side_4', room.roomId!),
            WallUtils.createWall(points[5], points[6], 'side_5', room.roomId!),
            WallUtils.createWall(points[6], points[7], 'side_6', room.roomId!),
            WallUtils.createWall(points[7], points[0], 'side_7', room.roomId!),
        ];
        return { room, roomWalls };
    };

    private static createPlusShapeRoom = (room: IRoom, currentWalls: Wall[]): CreateResult => {
        //* define points coordinates

        let points: Array<CoordPoint>;
        if (room.coords) {
            points = room.coords;
        } else {
            points = room.customPoints!;

            if (room.center) {
                PointUtils.translationByMeanPoint(points, room.center);
            }

            points.forEach((point) => PointUtils.convertToCoordinates(point));

            if (!room.center) {
                PointUtils.placeToZero(points);
                PointUtils.placeAlongSideExistingRooms(points, currentWalls);
            }
            PointUtils.smoothOutPoints(points);
            room.coords = [...points, points[0]];
        }

        room.shape = RoomShape.Custom;
        const roomWalls = [
            WallUtils.createWall(points[0], points[1], 'side_0', room.roomId!),
            WallUtils.createWall(points[1], points[2], 'side_1', room.roomId!),
            WallUtils.createWall(points[2], points[3], 'side_2', room.roomId!),
            WallUtils.createWall(points[3], points[4], 'side_3', room.roomId!),
            WallUtils.createWall(points[4], points[5], 'side_4', room.roomId!),
            WallUtils.createWall(points[5], points[6], 'side_5', room.roomId!),
            WallUtils.createWall(points[6], points[7], 'side_6', room.roomId!),
            WallUtils.createWall(points[7], points[8], 'side_7', room.roomId!),
            WallUtils.createWall(points[8], points[9], 'side_8', room.roomId!),
            WallUtils.createWall(points[9], points[10], 'side_9', room.roomId!),
            WallUtils.createWall(points[10], points[11], 'side_10', room.roomId!),
            WallUtils.createWall(points[11], points[0], 'side_11', room.roomId!),
        ];

        return { room, roomWalls };
    };

    private static createCustomShapeRoom = (room: IRoom, currentWalls: Wall[]): CreateResult => {
        const points = _.cloneDeep(room.coords?.slice(0, room.coords!.length - 1) || room.customPoints || []);
        if (!room.coords) {
            const walls = PointUtils.toWalls(points);

            const sides = new Map();
            let wallCount = 0;
            walls.forEach((wall) => {
                sides.set(`side_${wallCount}`, [wall.start, wall.end]);
                wallCount++;
            });
            room.sides = sides;

            if (room.center) {
                PointUtils.translationByMeanPoint(points, room.center);
            }

            points.forEach((point) => PointUtils.convertToCoordinates(point));

            PointUtils.smoothOutPoints(points);
            room.coords = [...points, points[0]];
        }
        room.shape = RoomShape.Custom;
        const roomWalls = [];
        for (let i = 0; i < points.length; i++) {
            roomWalls.push(WallUtils.createWall(points[i], points[(i + 1) % points.length], `side_${i}`, room.roomId!));
        }
        return { room, roomWalls };
    };

    //#endregion

    private static exportCustomShapeRoom(room: IRoom): IRoom | undefined {
        if ([RoomType.Sol, RoomType.Wall].includes(room.type!)) {
            const points = ObjectUtils.clone(room.coords!.slice(0, room.coords!.length - 1));
            const roomWalls = PointUtils.toWalls(points);

            const sides = new Map();
            let wallCount = 0;
            roomWalls.forEach((wall) => {
                sides.set(`side_${wallCount}`, [wall.start, wall.end]);
                wallCount++;
            });

            points.forEach((point) => (point = this.convertToMeters(point)));
            const mp = PointUtils.meanPoint(points);
            PointUtils.translationByMeanPoint(points, mp, true);

            return this.exportRoomObject(room, mp, points);
        }
        if ([RoomType.Stairs].includes(room.type!)) {
            return this.exportRoomObject(room);
        }
    }

    private static exportRoomObject(room: IRoom, center?: CoordPoint, points?: Array<CoordPoint>): IRoom {
        return {
            ...room,
            center: center,
            customPoints: points,
        };
    }

    //#endregion

    //#region //* GET ROOM SIDES

    public static toRoomSides = (room: IRoom): Map<string, Array<CoordPoint>> => {
        return new Map();
    };

    //#endregion

    public static convertToMeters(point: CoordPoint) {
        point.x /= MapConstants.meter;
        point.y /= MapConstants.meter;
        return point;
    }

    public static rotateRoom(
        //TODO À REFAIRE cf. apple note
        targetRoomId: string,
        angleDegree: number,
        allRooms: IRoom[],
        allWalls: Wall[]
    ): {
        newAllWalls: Array<Wall>;
        newAllRooms: IRoom[];
        roomOpenings: IOpening[];
        roomItems: IRoomItem[];
    } {
        const room = allRooms.find((x) => x.roomId === targetRoomId);
        const newTargetRoomPoints = room!.walls!.map((x) => x.start);
        const roomItems = room!.roomItems;
        const roomOpenings = room!.openings;
        const rotationCenter = PointUtils.bboxCenter(newTargetRoomPoints!);
        const rad = PointUtils.degreeToRad(angleDegree);

        PointUtils.rotation(newTargetRoomPoints!, angleDegree, rotationCenter);

        const otherRoomsWalls = allWalls.filter((x) => x.roomId !== targetRoomId);

        const { newAllWalls, newAllRooms } = WallUtils.rebuildWalls(
            newTargetRoomPoints,
            allRooms,
            otherRoomsWalls,
            targetRoomId!
        );
        const invalidRoomIds = InvalidPositionDetection.getInvalidRoomIds(newAllRooms, targetRoomId);
        const needToMoveForMoreSpace = invalidRoomIds.length > 0;

        let offset: CoordPoint = { x: 0, y: 0 };
        if (needToMoveForMoreSpace) {
            const otherRoomsBbox = PointUtils.calculateBoundingBox({ walls: otherRoomsWalls })!;
            const roomBbox = PointUtils.calculateBoundingBox({ points: newTargetRoomPoints })!;
            offset = {
                x: otherRoomsBbox.xMax - roomBbox.xMin + MapConstants.roomDefaultSpacing,
                y: otherRoomsBbox.yMin - roomBbox.yMin,
            };
        }

        if (roomItems) {
            for (const roomItem of roomItems) {
                PointUtils.rotation(roomItem.coordsReal!, angleDegree, rotationCenter);
                // const bboxReal = PointUtils.calculateBoundingBox({ points: roomItem.coordsReal! });
                // roomItem.coordsReal = [
                //     { x: bboxReal!.xMin, y: bboxReal!.yMin },
                //     { x: bboxReal!.xMax, y: bboxReal!.yMin },
                //     { x: bboxReal!.xMax, y: bboxReal!.yMax },
                //     { x: bboxReal!.xMin, y: bboxReal!.yMax },
                //     { x: bboxReal!.xMin, y: bboxReal!.yMin },
                // ];
                // roomItem.coordsReal = roomItem.coordsReal!.map((p) => PointUtils.translation(p, offset));
                PointUtils.singleRotation(roomItem.graphTranslation!, rad, rotationCenter);
                roomItem.room_elements_length =
                    PerimeterCalc.roomItemDetailedPerimeter(roomItem, [room!]).lengthNotAdjacentToWall / 100;
            }
        }
        if (roomOpenings) {
            for (const opening of roomOpenings) {
                const coord = { x: opening.x!, y: opening.y! };
                PointUtils.singleRotation(coord, rad, rotationCenter);
                opening.x = coord.x + offset.x;
                opening.y = coord.y + offset.y;
            }
        }

        if (needToMoveForMoreSpace && offset) {
            return {
                ...WallUtils.rebuildWalls(
                    newTargetRoomPoints.map((point) => PointUtils.translation(point, offset)),
                    allRooms,
                    otherRoomsWalls,
                    targetRoomId!
                ),
                roomOpenings: roomOpenings!,
                roomItems: roomItems!,
            };
        }
        return { newAllWalls, newAllRooms, roomOpenings: roomOpenings!, roomItems: roomItems! };
    }

    public static getCoordsFromWalls = (room: IRoom): Array<CoordPoint> => {
        if (room.walls) {
            const coords = room.walls.map((wall) => wall.start);
            return [...coords, coords[0]]; // close the polygon
        }
        return [];
    };
}

const toShape = (room: IRoom): RoomShape => {
    if (
        ['flyingwing_bottomleft', 'flyingwing_bottomright', 'flyingwing_topleft', 'flyingwing_topright'].includes(
            room.shape!
        )
    ) {
        return RoomShape.Flyingwing;
    }
    return room.shape!;
};
